/**
 * A Silvus MANET's links: who can hear whom, and how well.
 *
 * The radios are on the operator's LAN, out of the server's reach, so the
 * connector on a laptop there reads them and pushes the picture. Numbers the
 * connector stopped refreshing are flagged as old rather than shown as live,
 * because a link that was good a minute ago says nothing about now.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { getSilvus, type LinkQuality, type SilvusPicture, type SilvusReading } from '@/api/mesh';

import { allNodes, edges, placeNodes } from './silvusLayout';

const OK = 'oklch(0.78 0.15 155)';
const WARN = 'oklch(0.80 0.15 85)';
const DANGER = 'oklch(0.72 0.19 28)';
const MUTED = 'rgba(244,244,245,0.5)';

const QUALITY_COLOR: Record<LinkQuality, string> = { good: OK, fair: WARN, poor: DANGER };
const REFRESH_MS = 3000;
const SIZE = 220;

function fixed(value: number | null, digits: number, unit: string): string {
  return value === null ? 'not reported' : `${value.toFixed(digits)} ${unit}`;
}

export function SilvusMeshPanel(): ReactNode {
  const [picture, setPicture] = useState<SilvusPicture | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    const load = async () => {
      const result = await getSilvus();
      if (!live) return;
      if (result.ok) {
        setPicture(result.data);
        setError(null);
      } else {
        setError(result.error?.message ?? 'Could not load the mesh picture.');
      }
    };
    void load();
    const timer = window.setInterval(() => void load(), REFRESH_MS);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, []);

  if (error !== null) {
    return (
      <p className="text-[11px]" style={{ color: WARN }}>
        {error}
      </p>
    );
  }
  if (picture === null) {
    return (
      <p className="text-[11px]" style={{ color: MUTED }}>
        Loading the mesh picture.
      </p>
    );
  }
  if (picture.readings.length === 0) {
    return (
      <p className="text-[11px] leading-relaxed" style={{ color: MUTED }}>
        No Silvus readings yet. On a laptop on the radios&rsquo; network, run{' '}
        <span className="font-mono">silvus_discover.py --push</span> from the Parallax Bridge
        folder.
        {picture.configured
          ? ''
          : ' The server also needs the feed secret, as INGEST_TOKEN_SILVUS_FIELD for the default feed id.'}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      {picture.readings.map((reading) => (
        <SilvusMesh key={reading.sourceId} reading={reading} />
      ))}
    </div>
  );
}

function SilvusMesh({ reading }: { reading: SilvusReading }): ReactNode {
  const nodes = placeNodes(allNodes(reading), SIZE);
  const at = new Map(nodes.map((node) => [node.nodeId, node]));
  const lines = edges(reading.links);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[12px] font-semibold">
          {reading.network ?? reading.sourceId}
        </span>
        <span className="font-mono text-[10px]" style={{ color: MUTED }}>
          {nodes.length} radios · via {reading.localIp ?? 'unknown address'} · updated{' '}
          {Math.round(reading.ageS)} s ago
        </span>
      </div>
      {reading.stale ? (
        <div className="text-[10.5px]" style={{ color: WARN }}>
          The connector stopped reporting. These are its last numbers, not the mesh now.
        </div>
      ) : null}

      <div className="flex flex-wrap items-start gap-4" style={{ opacity: reading.stale ? 0.55 : 1 }}>
        <svg
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className="h-auto max-w-full shrink-0"
          role="img"
          aria-label={`Silvus mesh topology, ${nodes.length} radios`}
        >
          {lines.map((edge) => {
            const a = at.get(edge.a);
            const b = at.get(edge.b);
            if (a === undefined || b === undefined) return null;
            return (
              <line
                key={`${edge.a}|${edge.b}`}
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                stroke={QUALITY_COLOR[edge.quality]}
                strokeWidth={edge.quality === 'good' ? 2.5 : 1.5}
                strokeDasharray={edge.quality === 'poor' ? '4 3' : undefined}
              >
                <title>{`${edge.snr.toFixed(0)} dB, the weaker direction`}</title>
              </line>
            );
          })}
          {nodes.map((node) => (
            <g key={node.nodeId}>
              <circle
                cx={node.x}
                cy={node.y}
                r={node.local ? 9 : 7}
                fill={node.local ? '#f4f4f5' : '#18181b'}
                stroke="#f4f4f5"
                strokeWidth={1.5}
              />
              <text
                x={node.x}
                y={node.y + (node.local ? 22 : 19)}
                textAnchor="middle"
                fontSize={9.5}
                fontFamily="Geist Mono, monospace"
                fill="rgba(244,244,245,0.8)"
              >
                {node.label}
              </text>
            </g>
          ))}
        </svg>

        <div className="flex min-w-[min(220px,100%)] flex-1 flex-col gap-1.5">
          <div className="flex gap-3 font-mono text-[10px]" style={{ color: MUTED }}>
            <span>Data rate {fixed(reading.totalRateMbps, 2, 'Mbps')}</span>
            <span>Air time {fixed(reading.totalAirTimePercent, 1, '%')}</span>
          </div>
          {reading.links.length === 0 ? (
            <p className="text-[11px]" style={{ color: MUTED }}>
              The radio reported no links: only the local unit is on the mesh.
            </p>
          ) : (
            reading.links.map((link) => (
              <div
                key={`${link.source}>${link.target}`}
                className="flex items-center gap-2 font-mono text-[10.5px]"
              >
                <span
                  className="size-[7px] shrink-0 rounded-full"
                  style={{ background: QUALITY_COLOR[link.quality] }}
                />
                <span className="w-[140px] min-w-0 shrink truncate" title={`${link.sourceLabel} to ${link.targetLabel}`}>
                  {link.sourceLabel} to {link.targetLabel}
                </span>
                <span className="tnum" style={{ color: QUALITY_COLOR[link.quality] }}>
                  {link.snr.toFixed(0)} dB
                </span>
                <span className="tnum ml-auto" style={{ color: MUTED }}>
                  {link.rateMbps === null ? '' : `${link.rateMbps.toFixed(2)} Mbps`}
                  {link.airTimePercent === null ? '' : ` · ${link.airTimePercent.toFixed(1)}% air`}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default SilvusMeshPanel;
